/**
 * Function that when called cancels the repeating frame.
 */
export interface FrameCancelFunction {
  /**
   * Cancels the repeating frame.
   */
  (): void;
}

/**
 * A function to be called every frame.
 */
export interface FrameCallback {
  /**
   * Executes the frame.
   */
  (): void;
}

/**
 * Schedules a repeating function to execute every animation frame until cancelled.
 * @param frame The frame to execute.
 * @param skip The optional number of frames to skip.
 * @returns A function that can be called to cancel the repeating frame.
 */

export function doFrameLoop(frame: FrameCallback, skip: number = 0): FrameCancelFunction {
  let animationFrameId: number | null = null;
  let frameCount = 0;
  let initial = true;

  function animateFrame(): void {
    animationFrameId = null;
    if (frameCount === 0 || initial) {
      frame();
      initial = false;
    }
    frameCount++;
    if (frameCount > skip) frameCount = 0;
    animationFrameId = requestAnimationFrame(animateFrame);
  }

  animateFrame();

  return () => {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
  };
}
